import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type { OutsideRecordSummary } from '../../services/api/types.gen';
import { outsideRecordByline } from './outsideRecord';

export interface OutsideRecordBadgeProps {
  outsideRecord: OutsideRecordSummary | null | undefined;
}

/**
 * Marks a visit completed with an outside record. Renders nothing otherwise.
 * Tap opens the tooltip too, since phones have no hover (as ExternalCustodyBadge).
 */
export const OutsideRecordBadge: React.FC<OutsideRecordBadgeProps> = ({
  outsideRecord,
}) => {
  const [open, setOpen] = useState(false);
  if (!outsideRecord) return null;

  const missing = !outsideRecord.has_document;
  const label = missing ? 'Outside record, document missing' : 'Outside record';
  const byline = outsideRecordByline(outsideRecord);

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span
            role='button'
            tabIndex={0}
            aria-label={`${label}. ${outsideRecord.reason}`}
            className='inline-flex shrink-0 cursor-help'
            onClick={event => {
              event.stopPropagation();
              setOpen(true);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                setOpen(true);
              }
            }}
            data-testid='outside-record-badge'
          >
            <Badge variant='outline' className='gap-1 whitespace-nowrap'>
              Outside record
              {missing ? (
                <AlertCircle
                  className='h-3.5 w-3.5 text-red-600'
                  aria-hidden='true'
                  data-testid='outside-record-missing-icon'
                />
              ) : null}
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent className='max-w-xs space-y-1'>
          <p>{outsideRecord.reason}</p>
          {byline ? <p className='text-muted-foreground'>{byline}</p> : null}
          {missing ? (
            <p>
              The outside record document is missing. Attach it from Actions →
              Outside record.
            </p>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default OutsideRecordBadge;
